<template>
  <div class="modelReg">
    <div class="modelReg__grid">
      <div class="modelReg__title">
        <h3>Восстановление пароля</h3>
      </div>

      <div v-if="loading" class="modelReg__alertLoading"></div>
      <div v-if="errorMessage" class="modelReg__alertError">{{ errorMessage }}</div>
      <div v-if="successMessage" class="modelReg__alertSuccess">{{ successMessage }}</div>

      <!-- Информация о методе -->
      <div class="info-section mb-4">
        <div class="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
          <UIcon name="i-heroicons-shield-check" class="w-6 h-6 text-blue-500 mt-1"/>
          <div>
            <h4 class="font-medium text-blue-900">Безопасный сброс пароля</h4>
            <p class="text-sm text-blue-700 mt-1">
              Откройте бота заведения в Telegram или MAX, подтвердите номер телефона — вам будет выдан новый пароль
            </p>
            <ul class="text-xs text-blue-600 mt-2 space-y-1">
              <li>• Проверка по номеру телефона</li>
              <li>• Ссылка действительна 10 минут</li>
              <li>• Новый пароль придёт в чат после подтверждения номера</li>
              <li>• Мессенджер откроется автоматически, если доступна одна ссылка</li>
            </ul>
          </div>
        </div>
      </div>

      <UForm class="modelReg__form" :state="form" autocomplete="off">
        <UiPhoneInputInternational
            v-model="form.phone"
            :data-testid="'e2e-auth-phone-input'"
            label="Номер телефона"
            :show-error="false"
            @change="v$.phone.$touch"
        />

        <UiButtonMain
            class="btn"
            color="red"
            size="xl"
            :label="loading ? 'Отправка...' : submitButtonLabel"
            block
            type="submit"
            :disabled="!canSubmit || loading"
            :loading="loading"
            @click.prevent="requestPasswordReset"
        />

        <NuxtLink
            v-if="successMessage && telegramLinkFromAPI"
            :to="telegramLinkFromAPI"
            target="_blank"
            class="flex items-center justify-center gap-4 px-4 py-3 text-center bg-black text-white rounded-full mt-3 telegram-link"
        >
          <UIcon name="file-icons:telegram" class="w-5 h-5"/>
          <span>Открыть Telegram</span>
        </NuxtLink>

        <a
            v-if="successMessage && maxLinkFromAPI"
            :href="maxLinkFromAPI"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center justify-center gap-4 px-4 py-3 text-center rounded-full mt-3 max-link"
            @click.prevent="openMax(maxLinkFromAPI)"
        >
          <span class="text-lg font-semibold" aria-hidden="true">MAX</span>
          <span>Открыть MAX</span>
        </a>

        <!-- Навигация -->
        <div class="text-center mt-4">
          <UButton
              color="sky"
              variant="link"
              label="Вернуться к входу"
              @click="modalStore.setState(modalStateEnum.Login)"
          />
        </div>
      </UForm>
    </div>
  </div>
</template>

<script setup lang="ts">
/* v8 ignore file */
import {ref, reactive, computed} from 'vue'
import { consola } from 'consola'
import {useVuelidate} from '@vuelidate/core'
import {isValidPhoneNumber} from 'libphonenumber-js'
import {helpers} from '@vuelidate/validators'
import {useModalStore,modalStateEnum} from '@shared/model'
import {useInstitutionStore, getReadyInstitutionId } from '@entities/institution'

import APIAuth from '~/entities/auth'
import type {PasswordResetRequestV2Params} from '~/entities/auth'


const logger = consola.withTag('forgot-password')
const config = useRuntimeConfig()
const modalStore = useModalStore()
const storeInstitution = useInstitutionStore()

const loading = ref(false)
const errorMessage = ref<string | null>(null)
const successMessage = ref<string | null>(null)
const telegramLinkFromAPI = ref<string | null>(null)
const maxLinkFromAPI = ref<string | null>(null)

const form = reactive({
  phone: '',
})

const rules = {
  phone: {
    required: helpers.withMessage('Телефон обязателен', (v: string) => !!v),
    phoneValid: helpers.withMessage('Неверный формат телефона', (value: string) => {
      if (!value) return false
      try {
        return isValidPhoneNumber(value)
      } catch {
        return false
      }
    }),
  },
}

const v$ = useVuelidate(rules, form)

const canSubmit = computed(() => !v$.value.$invalid)

const submitButtonLabel = computed(() => {
  if (successMessage.value && (telegramLinkFromAPI.value || maxLinkFromAPI.value)) {
    return 'Отправить ссылку снова'
  }
  return 'Получить ссылку в мессенджере'
})

const openMax = (url: string) => {
  try {
    window.open(url, '_blank')
  } catch (error) {
    logger.error('Ошибка при открытии MAX:', error)
  }
}

const requestPasswordReset = async () => {
  if (!canSubmit.value) return

  loading.value = true
  errorMessage.value = null
  successMessage.value = null

  try {
    await getReadyInstitutionId();
    const payload: PasswordResetRequestV2Params = {
      phoneNumber: form.phone,
      institutionId: config.public.institutionID,
    }

    const response = await APIAuth.requestPasswordReset(payload)

    telegramLinkFromAPI.value = response.data.telegramLink || null
    maxLinkFromAPI.value = response.data.maxLink || null

    successMessage.value =
      response.data.message ||
      'Ссылка для сброса пароля сформирована. Выберите мессенджер ниже или дождитесь автоматического открытия.'

    const linksCount = Number(!!telegramLinkFromAPI.value) + Number(!!maxLinkFromAPI.value)
    if (linksCount === 1) {
      setTimeout(() => {
        try {
          const url = telegramLinkFromAPI.value || maxLinkFromAPI.value!
          const w = window.open(url, '_blank')
          if (!w) {
            logger.warn('Мессенджер не удалось открыть автоматически (блокировщик всплывающих окон)')
          }
        } catch (error) {
          logger.error('Ошибка при открытии мессенджера:', error)
        }
      }, 800)
    }
  } catch (error: unknown) {
    logger.error('Ошибка запроса сброса пароля:', error)
    const err = error as { data?: { message?: string } }
    errorMessage.value = err?.data?.message || 'Ошибка при отправке запроса'
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  try {
    if (!storeInstitution.institution) {
      const institutionID = await getReadyInstitutionId();
      await storeInstitution.fetchBranches(institutionID)
    }
  } catch (error) {
    logger.error('Ошибка загрузки данных учреждения:', error)
  }
})
</script>

<style scoped lang="scss">
.info-section {
  .info-card {
    @apply transition-colors duration-200;
  }
}

.info-blocks {
  .info-card {
    @apply transition-colors duration-200;
  }
}

.telegram-link {
  @apply transition-all duration-200 hover:bg-gray-800;

  &:hover {
    transform: translateY(-1px);
  }
}

.max-link {
  background: linear-gradient(135deg, #2d7eff 0%, #6b4dff 100%);
  @apply text-white transition-all duration-200;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }
}

.success-message {
  @apply animate-pulse;
}
</style>
